/**
 * <jsc-bundle-selector>
 *
 * Manages all bundle-selector logic: variant selection, price rendering, colour availability,
 * OOS auto-select, Swiper carousel, tab/slider toggle, ATC with SG mini-cart integration,
 * and browser URL sync.
 *
 * Data sources (read on connectedCallback + DOMContentLoaded):
 *   #product       — #pdp-product-json        (child of this element)
 *   #hideFlags     — #variant-hide-metafield   (child of this element)
 *   #settings      — [data-jsc-bundle-config]  (child of this element)
 *   #bundleContentMap — included inside [data-jsc-bundle-config] as bundleContentMap
 *   #moneyFormat   — data-money-format attribute on this element
 *
 * Requires: window.Swiper (Swiper 8, loaded before this script via swiper-bundle.min.js)
 */
(function () {
  class JscBundleSelector extends HTMLElement {
    // ── Private state ────────────────────────────────────────────────────
    #product      = null   // full Shopify product object
    #hideFlags    = {}     // { variantId: true/false }
    #settings     = {}     // from data-jsc-bundle-config
    #bundleContentMap = {}  // { variantId: htmlString } from custom.bundle_description_html metafield
    #currentVariant = null // currently selected variant object (or null)
    #swiper       = null   // Swiper instance for bundle cards carousel
    #isTabMode    = false  // true → tab layout; false → swiper carousel
    #isMobile     = false  // true if viewport < 992 px
    #singleBundle = false  // true if only one deal option exists
    #moneyFormat  = ''     // Shopify money format string, e.g. "${{amount}}"
    #colorIdx     = 0      // index of "Color" in product.options
    #dealIdx      = 1      // index of "Deal" in product.options

    // ── Lifecycle ────────────────────────────────────────────────────────
    connectedCallback() {
      this.#readConfig()

      const init = () => {
        this.#safeStep('detectEnvironment',     () => this.#detectEnvironment())
        this.#safeStep('attachListeners',       () => this.#attachListeners())
        this.#safeStep('disableUnusableColors', () => this.#disableUnusableColors())
        this.#safeStep('syncVariant',           () => this.#syncVariant())
        this.#safeStep('updateColorCrossedOut', () => this.#updateColorCrossedOut())
        this.#safeStep('refreshBundleCards',    () => this.#refreshBundleCards())
        this.#safeStep('updateAtcButton',       () => this.#updateAtcButton())
        this.#safeStep('updateUrl',             () => this.#updateUrl())
        if (this.#isTabMode) this.#safeStep('syncTabContent', () => this.#syncTabContent())
        this.#safeStep('autoSelectIfOos', () => this.#autoSelectIfOos())
        if (!this.#isTabMode && !this.#singleBundle) {
          this.#safeStep('initSwiper', () => this.#initSwiper())
        }
        this.#safeStep('initColorSwatches', () => this.#initColorSwatches())
      }

      if (document.readyState !== 'complete') {
        document.addEventListener('DOMContentLoaded', init, { once: true })
      } else {
        init()
      }
    }

    /** Run a lifecycle step in isolation so one throwing step can't skip the rest (e.g. Swiper never getting initialised). */
    #safeStep(name, fn) {
      try {
        fn()
      } catch (e) {
        console.error(`[jsc-bundle-selector] step "${name}" threw — continuing init:`, e)
      }
    }

    // ── Data reading ─────────────────────────────────────────────────────
    #readConfig() {
      try {
        const productEl = this.querySelector('#pdp-product-json')
        if (productEl) this.#product = JSON.parse(productEl.textContent)

        const hideEl = this.querySelector('#variant-hide-metafield')
        if (hideEl) this.#hideFlags = JSON.parse(hideEl.textContent)

        const configEl = document.querySelector('[data-jsc-bundle-config]')
        if (configEl) {
          const cfg = JSON.parse(configEl.textContent)
          this.#settings        = cfg
          this.#bundleContentMap = cfg.bundleContentMap ?? {}
        }

        this.#moneyFormat = this.dataset.moneyFormat ?? ''

        if (this.#product) {
          const opts = this.#product.options ?? []
          const ci = opts.indexOf('Color')
          const di = opts.indexOf('Deal')
          this.#colorIdx = ci >= 0 ? ci : 0
          this.#dealIdx  = di >= 0 ? di : 1
        }
      } catch (e) {
        console.error('[jsc-bundle-selector] Failed to read config:', e)
      }
    }

    // ── Environment detection ─────────────────────────────────────────────
    #detectEnvironment() {
      this.#isMobile = window.innerWidth < 992

      const labels = [...document.querySelectorAll('.machine-pdp-deal-variant label')]
      this.#singleBundle = labels.length === 1
      if (this.#singleBundle) {
        document.querySelector('.machine-pdp-deal-variant')?.classList.add('single-bundle')
      }

      const tabs = document.querySelector('.machine-pdp-deal-variant.machine-pdp-variant-tabs')
      if (tabs) {
        const visibleCount = labels.filter(l => l.getAttribute('hide-storefront') !== 'true').length
        if (visibleCount > 3 || this.#isMobile) {
          tabs.classList.remove('machine-pdp-variant-tabs')
          tabs.classList.add('machine-pdp-variant-slider')
          this.#isTabMode = false
        } else {
          this.#isTabMode = true
        }
      } else {
        this.#isTabMode = false
      }
    }

    // ── Core helpers ──────────────────────────────────────────────────────

    /** Find a variant matching all provided option values (by position). */
    #findVariant(optionsArray) {
      if (!this.#product) return null
      return this.#product.variants.find(v =>
        optionsArray.every((val, i) => v.options[i] === val)
      ) ?? null
    }

    /** Return true if this variant should be hidden from the storefront. */
    #isHidden(variantId) {
      const v = this.#hideFlags[variantId]
      return v === true || v === 'true' || v === 1 || v === '1'
    }

    /** Format a price in cents using the theme currency formatter. */
    #formatMoney(cents) {
      if (!cents && cents !== 0) return ''
      if (window.theme?.Currency?.formatMoney) {
        return window.theme.Currency.formatMoney(cents, this.#moneyFormat)
      }
      // Fallback for environments where theme.Currency isn't loaded yet
      return '$' + (cents / 100).toFixed(2)
    }

    // ── Bundle card rendering (A) ─────────────────────────────────────────
    #refreshBundleCards() {
      const colorInput = document.querySelector('.machine-pdp-color-variant input:checked')
      const dealInputs = [...document.querySelectorAll('.machine-pdp-deal-variant input[type="radio"]')]

      dealInputs.forEach(input => {
        const label = document.querySelector(`label[for="${input.id}"]`)
        if (!label) return

        const dealValue    = input.value
        const selectedColor = colorInput?.value ?? ''
        const variant       = selectedColor ? this.#findVariant([selectedColor, dealValue]) : null

        if (!variant) {
          // Combination doesn't exist
          label.classList.add('not-exist', 'pushed-back', 'd-none')
          label.classList.remove('swiper-slide')
          const priceEl = label.querySelector('.variant-bundle-price')
          if (priceEl) priceEl.textContent = ''
          return
        }

        label.classList.remove('not-exist', 'pushed-back', 'd-none')
        label.classList.add('swiper-slide')

        const content = label.querySelector('.slider-view-bundle-content')
        if (content) {
          const mapContent = this.#bundleContentMap[variant.id]
          if (mapContent != null) content.innerHTML = mapContent
        }

        // Prices
        const priceEl    = label.querySelector('.variant-bundle-price')
        const oldPriceEl = label.querySelector('.variant-bundle-old-price')
        if (priceEl) priceEl.innerHTML = this.#formatMoney(variant.price)
        if (oldPriceEl) {
          oldPriceEl.innerHTML = variant.compare_at_price > 0
            ? this.#formatMoney(variant.compare_at_price)
            : ''
        }

        // Hide-from-storefront
        const hidden = this.#isHidden(variant.id)
        label.setAttribute('hide-storefront', hidden ? 'true' : 'false')
        if (hidden) input.disabled = true

        // Show-more indicator
        if (content && content.scrollHeight > content.clientHeight + 10) {
          label.classList.add('show-more-available')
        } else {
          label.classList.remove('show-more-available')
        }
      })

      this.#swiper?.update()
    }

    // ── Variant selection (M) ─────────────────────────────────────────────
    #syncVariant() {
      const colorInput = document.querySelector('.machine-pdp-color-variant input:checked')
      const dealInput  = document.querySelector('.machine-pdp-deal-variant input:checked')

      let variant = null
      if (colorInput && dealInput) {
        variant = this.#findVariant([colorInput.value, dealInput.value])
        if (variant && this.#isHidden(variant.id)) variant = null
      }

      if (variant) {
        this.#currentVariant = variant
        const sel = document.querySelector('select.product-form-selector')
        if (sel) sel.value = variant.id
        document.dispatchEvent(new CustomEvent('theme:variant:change', {
          bubbles: true,
          detail: { variant }
        }))

        if (variant.featured_media?.id) {
          const section = this.closest('[data-section-id]')
          const slideshow = section?.querySelector('[data-product-slideshow]')
          if (slideshow) {
            slideshow.dispatchEvent(new CustomEvent('theme:image:change', {
              detail: { id: variant.featured_media.id }
            }))
          }
        }
      } else {
        // Fallback: first globally available, non-hidden variant
        const fallback = this.#product?.variants.find(v => v.available && !this.#isHidden(v.id))
        if (fallback) this.#autoClickVariant(fallback)
      }
    }

    /** Re-run variant sync + dependent UI updates after the checked radio changes (click or Swiper-driven). */
    #applyVariantChange() {
      this.#syncVariant()
      this.#updateColorCrossedOut()
      this.#updateAtcButton()
      this.#updateUrl()
    }

    // ── Colour crossed-out state (j) ──────────────────────────────────────
    #updateColorCrossedOut() {
      const dealInput  = document.querySelector('.machine-pdp-deal-variant input:checked')
      const dealValue  = dealInput?.value ?? ''
      const colorInputs = [...document.querySelectorAll('.machine-pdp-color-variant input[type="radio"]')]

      colorInputs.forEach(input => {
        const wrapper = input.closest('.variant-option')
        if (!wrapper) return

        const variant = this.#findVariant([input.value, dealValue])
        if (!variant) {
          wrapper.classList.add('crossed-out')
        } else if (!variant.available || this.#isHidden(variant.id)) {
          wrapper.classList.add('crossed-out')
        } else {
          wrapper.classList.remove('crossed-out')
        }
      })
    }

    // ── Disable colours with no valid bundles (C) ─────────────────────────
    #disableUnusableColors() {
      const dealValues  = [...document.querySelectorAll('.machine-pdp-deal-variant input[type="radio"]')]
        .map(i => i.value)
      const colorInputs = [...document.querySelectorAll('.machine-pdp-color-variant input[type="radio"]')]

      colorInputs.forEach(input => {
        const hasAnyDeal = dealValues.some(deal => {
          const v = this.#findVariant([input.value, deal])
          return v && v.available && !this.#isHidden(v.id)
        })
        const wrapper = input.closest('.variant-option')
        if (!hasAnyDeal) {
          input.disabled = true
          wrapper?.classList.add('d-none')
        }
      })
    }

    // ── ATC button state (O) ──────────────────────────────────────────────
    #updateAtcButton() {
      const btn     = document.querySelector('.pdp-atc')
      const nameEl  = document.querySelector('.pdp-bundle-name')
      const checked = document.querySelector('.machine-pdp-deal-variant input:checked')

      if (nameEl && checked) nameEl.textContent = checked.value

      if (btn) {
        const available = this.#currentVariant?.available === true
          && !this.#isHidden(this.#currentVariant?.id)
        btn.disabled = !available
      }
    }

    // ── URL history (I) ───────────────────────────────────────────────────
    #updateUrl() {
      if (!this.#currentVariant) return
      try {
        const url = new URL(window.location.href)
        url.searchParams.set('variant', this.#currentVariant.id)
        window.history.replaceState({}, '', url.toString())
      } catch (_) {}
    }

    // ── Tab content sync (T) ──────────────────────────────────────────────
    #syncTabContent() {
      const tabView    = document.querySelector('.tab-view-bundle-content')
      const checked    = document.querySelector('.machine-pdp-deal-variant input:checked')
      if (!tabView || !checked) return

      tabView.classList.remove('d-none')

      const label         = document.querySelector(`label[for="${checked.id}"]`)
      const sliderContent = label?.querySelector('.slider-view-bundle-content')
      tabView.innerHTML   = sliderContent?.innerHTML ?? ''

      const isOos = !this.#currentVariant?.available
        || this.#isHidden(this.#currentVariant?.id ?? -1)
      tabView.classList.toggle('out-of-stock', isOos)
    }

    // ── OOS auto-select ───────────────────────────────────────────────────
    #autoSelectIfOos() {
      if (this.#currentVariant?.available && !this.#isHidden(this.#currentVariant.id)) return
      if (!this.#product) return

      const colorInput = document.querySelector('.machine-pdp-color-variant input:checked')
      const dealInput  = document.querySelector('.machine-pdp-deal-variant input:checked')
      const strategy   = this.#settings.out_of_stock_select ?? 'color'

      let alt = null
      if (strategy === 'bundle' && colorInput) {
        // Keep same colour, try a different deal
        alt = this.#product.variants.find(v =>
          v.available && !this.#isHidden(v.id)
          && v.options[this.#colorIdx] === colorInput.value
        )
      } else if (dealInput) {
        // Keep same deal, try a different colour
        alt = this.#product.variants.find(v =>
          v.available && !this.#isHidden(v.id)
          && v.options[this.#dealIdx] === dealInput.value
        )
      }

      // Global fallback
      alt ??= this.#product.variants.find(v => v.available && !this.#isHidden(v.id))
      if (alt) this.#autoClickVariant(alt)
    }

    // ── Programmatic variant selection (B) ────────────────────────────────
    #autoClickVariant(variant) {
      const colorId = `Option${this.#colorIdx + 1}-${variant.options[this.#colorIdx]}`
      const dealId  = `Option${this.#dealIdx  + 1}-${variant.options[this.#dealIdx]}`

      const colorInput = document.querySelector(`.machine-pdp-color-variant input[id="${colorId}"]`)
      const dealInput  = document.querySelector(`.machine-pdp-deal-variant input[id="${dealId}"]`)

      colorInput?.click()
      dealInput?.click()

      if (this.#swiper && dealInput) {
        const pos = parseInt(dealInput.dataset.valuePosition ?? '0', 10)
        this.#swiper.slideTo(pos, 0)
      }
    }

    // ── Swiper (bundle cards carousel) ───────────────────────────────────
    #initSwiper() {
      if (!window.Swiper) {
        console.warn('[jsc-bundle-selector] window.Swiper not found — bundle carousel disabled')
        return
      }

      this.#swiper = new window.Swiper(
        '.machine-pdp-options-container .machine-pdp-deal-variant',
        {
          slidesPerView: 'auto',
          spaceBetween: 15,
          rewind: true,
          allowTouchMove: this.#isMobile,
          centeredSlides: true,
          centeredSlidesBounds: true,
          pagination: {
            el: '.machine-pdp-options-container .pdp-bundles-pagination',
            type: 'bullets',
            clickable: true
          },
          navigation: {
            prevEl: '.machine-pdp-options-container .pdp-bundle-prev',
            nextEl: '.machine-pdp-options-container .pdp-bundle-next'
          }
        }
      )

      // When the carousel moves, click the radio behind the newly-active slide
      this.#swiper.on('slideChange', swiper => {
        const slide = swiper.slides[swiper.activeIndex]
        const radio = slide?.previousElementSibling
        if (radio?.type === 'radio' && !radio.checked) {
          // Avoid radio.click() here — its 'change' event isn't reliable from inside a Swiper touch callback.
          radio.checked = true
          this.#applyVariantChange()
        }
      })

      // Slide to whichever deal is currently checked
      const checked = document.querySelector('.machine-pdp-deal-variant input:checked')
      if (checked) {
        const pos = parseInt(checked.dataset.valuePosition ?? '0', 10)
        this.#swiper.slideTo(pos, 0)
      }
    }

    // ── Event listeners ───────────────────────────────────────────────────
    #attachListeners() {
      // Radio buttons — colour and deal
      document.querySelectorAll(
        '.machine-pdp-color-variant input[type="radio"], .machine-pdp-deal-variant input[type="radio"]'
      ).forEach(input => {
        input.addEventListener('change', e => {
          const isColorChange = !!e.target.closest('.machine-pdp-color-variant')

          if (isColorChange) {
            this.#refreshBundleCards()
            if (this.#isTabMode) this.#syncTabContent()
          }

          this.#applyVariantChange()

          if (!isColorChange) {
            if (this.#isTabMode) this.#syncTabContent()
            // Sync Swiper to newly-checked deal
            if (this.#swiper) {
              const pos = parseInt(e.target.dataset.valuePosition ?? '0', 10)
              this.#swiper.slideTo(pos)
            }
          }
        })
      })

      // Add to Cart button
      document.querySelector('.pdp-atc')
        ?.addEventListener('click', e => this.#handleAtcClick(e))

      // Mobile crossed-out tooltip (2-second hover-on class)
      if (this.#isMobile) {
        document.querySelectorAll('.machine-pdp-color-variant .variant-option').forEach(wrapper => {
          let timer
          wrapper.addEventListener('touchstart', () => {
            if (!wrapper.classList.contains('crossed-out')) return
            wrapper.classList.add('hover-on')
            clearTimeout(timer)
            timer = setTimeout(() => wrapper.classList.remove('hover-on'), 2000)
          }, { passive: true })
        })
      }
    }

    // ── Add to Cart ───────────────────────────────────────────────────────
    #handleAtcClick(event) {
      event.preventDefault()
      if (!this.#currentVariant) return

      const btn = event.currentTarget
      btn.disabled = true

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: this.#currentVariant.id,
          quantity: 1,
          properties: {}
        })
      })
        .then(r => r.ok ? fetch('/cart.js').then(r2 => r2.json()) : null)
        .then(cart => {
          if (cart) {
            document.dispatchEvent(new CustomEvent('theme:cart:change', {
              bubbles: true,
              detail: { cart }
            }))
          }
        })
        .catch(err => console.error('[jsc-bundle-selector] Cart add failed:', err))
        .finally(() => { btn.disabled = false })
    }

    // ── Color swatch initialisation ───────────────────────────────────────
    async #initColorSwatches() {
      const url = window.theme?.assets?.swatches;
      if (!url) return
      try {
        const { colors } = await fetch(url).then(r => r.json())
        const map = {}
        colors.forEach(obj => {
          const [name, val] = Object.entries(obj)[0]
          map[name.toLowerCase()] = val
        })
        document.querySelectorAll('.machine-pdp-color-variant .swatch__button').forEach(span => {
          const label = span.querySelector('label[data-swatch]')
          if (!label) return
          const hex = map[label.dataset.swatch.toLowerCase()]
          if (hex) span.style.setProperty('--swatch', hex)
        })
      } catch (_) {}
    }
  }

  customElements.define('jsc-bundle-selector', JscBundleSelector)
})()
